import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { MDBCol, MDBRow } from "mdbreact";

import AbsModal from "../../common/absModal/AbsModal";
import AutocompleteDropdown from "../../common/autocompleteDropdown/AutocompleteDropdown";
import { Label, Input, Button } from "abs-ui-components";
import { emitToasterMsg } from "../../services/toasterUtility";
import { createUKETSAER } from "../../services/UKETSAerService";
import { useSelector } from "react-redux";
import axiosHttp from "../../services/interceptor";

export const getUKETSWCNList = async (searchStr = "", email) => {
	const baseURL = process.env.REACT_APP_BASE_URL;
	try {
		const requestedPayload = { searchKey: searchStr };
		const response = await axiosHttp.post(`${baseURL}api/Wcn/SearchWcn`, requestedPayload);
		return (response?.data || []).map((item) => ({
			label: `${item.WcnNumber} - ${item.CompanyName}`,
			value: item.WcnNumber,
			wcnMstId: item.WcnMstId,
			companyName: item.CompanyName,
		}));
	} catch (error) {
		console.error("Error fetching WCN options:", error);
		return [];
	}
};

const CreateUKETSAerModal = ({ isOpen, reportingYear, onClose, onSuccess }) => {
	const {
		control,
		handleSubmit,
		reset,
		clearErrors,
		formState: { errors },
	} = useForm({
		defaultValues: {
			wcn: null,
			companyName: "",
			companyImoNumber: "",
			reasonForCreation: "",
		},
	});

	const user = useSelector((state) => state.userInfo.userData);
	const email = user ? user?.email : null;

	const [isSubmitting, setIsSubmitting] = useState(false);
	// Controls whether the "Reason for Creation" field is visible (shown after duplication error)
	const [showReasonField, setShowReasonField] = useState(false);
	// Stores the last submitted form data so we can resubmit with reason on "Proceed"
	const [pendingPayload, setPendingPayload] = useState(null);
	const [reasonForCreation, setReasonForCreation] = useState("");
	const [reasonError, setReasonError] = useState("");

	const callCreateAPI = async (payload) => {
		setIsSubmitting(true);
		try {
			const result = await createUKETSAER(payload);

			if (result?.isSuccess) {
				emitToasterMsg("success", "UK ETS AER Created", `Report created successfully.`);
				handleClose();
				if (onSuccess) {
					onSuccess(result);
				}
				return;
			}

			const backendMessage = result?.errorMessage || "Unable to create the UK ETS Annual Emission Report.";
			const isDuplicationError = backendMessage.toLowerCase().includes("please provide a reason for creation");

			if (isDuplicationError) {
				// Show the reason field instead of a toaster for duplication
				setShowReasonField(true);
				setPendingPayload(payload);
			} else {
				emitToasterMsg("error", "Creation Failed", backendMessage);
			}
		} catch (error) {
			emitToasterMsg("error", "Error", error?.message || "Unable to create the UK ETS Annual Emission Report.");
		} finally {
			setIsSubmitting(false);
		}
	};

	// Called on initial "Create" button click
	const onSubmit = async (data) => {
		const payload = {
			wcnNumber: data.wcn?.value || "",
			companyImoNumber: String(data.companyImoNumber || "").trim(),
			companyName: String(data.companyName || "").trim(),
			reportYear: Number(reportingYear),
			reasonForCreation: "",
		};
		await callCreateAPI(payload);
	};

	// Called on "Proceed" button click after duplication error
	const onProceed = async () => {
		if (!reasonForCreation.trim()) {
			setReasonError("Reason for Creation is required.");
			return;
		}
		setReasonError("");
		const payload = {
			...pendingPayload,
			reasonForCreation: reasonForCreation.trim(),
		};
		await callCreateAPI(payload);
	};

	const handleClose = () => {
		reset();
		clearErrors();
		setShowReasonField(false);
		setPendingPayload(null);
		setReasonForCreation("");
		setReasonError("");
		onClose(false);
	};

	const ErrorMsg = ({ name }) =>
		errors[name] ? (
			<div
				className="error-msg"
				style={{
					fontSize: "12px",
					color: "#dc1e34",
					marginTop: "4px",
				}}
			>
				{errors[name].message}
			</div>
		) : null;

	return (
		<AbsModal isOpen={isOpen} title="Create UK ETS Annual Emission Report" onClose={onClose} close={handleClose}>
			<form onSubmit={handleSubmit(onSubmit)} className="p-3">

				<MDBRow className="mb-4 mt-4">
					<MDBCol md="12">
						<Controller
							name="wcn"
							control={control}
							rules={{
								required: "WCN is required",
							}}
							render={({ field }) => (
								<>
									<AutocompleteDropdown
										isClearable
										value={field.value ?? null}
										onChange={field.onChange}
										loadOptions={(searchStr) => getUKETSWCNList(searchStr, email)}
										placeholder="Please Select WCN"
										className="input-content mt-2 w-100"
										customStyles={{
											menuList: (provided) => ({
												...provided,
												maxHeight: 160,
											}),
										}}
									/>
									<ErrorMsg name="wcn" />
								</>
							)}
						/>
					</MDBCol>
				</MDBRow>

				<MDBRow className="mb-4">
					<MDBCol md="4">
						<Label name="Company IMO Number" bold required  />
						<Controller
							name="companyImoNumber"
							control={control}
							rules={{
								required: "Company IMO Number is required",
								pattern: {
									value: /^\d{7}$/,
									message: "IMO Number must be exactly 7 digits",
								},
							}}
							render={({ field }) => (
								<>
									<Input
										{...field}
										type="text"
										maxLength={7}
										placeholder=""
										   className="input-content mt-2"
										onKeyDown={(e) => {
											if (!/[0-9]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
												e.preventDefault();
											}
										}}
									/>
									<ErrorMsg name="companyImoNumber" />
								</>
							)}
						/>
					</MDBCol>

					<MDBCol md="8">
						<Label name="Company Name" bold required  />
						<Controller
							name="companyName"
							control={control}
							rules={{
								required: "Company Name is required",
							}}
							render={({ field }) => (
								<>
									<Input {...field} placeholder=""    
									className="input-content mt-2" />
									<ErrorMsg name="companyName" />
								</>
							)}
						/>
					</MDBCol>
				</MDBRow>

				{showReasonField && (
					<MDBRow className="mb-4">
						<MDBCol md="12">
							<Label name="Reason for Creating a Duplicate Report for the Same WCN and Company IMO Number" bold required />

							<Input
								value={reasonForCreation}
								onChange={(e) => {
									const value = typeof e === "string" ? e : (e?.target?.value ?? "");

									setReasonForCreation(value);

									if (value.trim()) {
										setReasonError("");
									}
								}}
								placeholder=""
								className="input-content mt-2"
							/>

							{reasonError && <div style={{ fontSize: "12px", color: "#dc1e34", marginTop: "4px" }}>{reasonError}</div>}
						</MDBCol>
					</MDBRow>
				)}

				<MDBRow className="mt-4">
					<MDBCol className="col-12 d-flex justify-content-end" style={{ gap: "8px" }}>
						{showReasonField ? (
							<>
								<Button type="button" disabled={isSubmitting} onClick={onProceed} className = "mt-2 p-2">
									{isSubmitting ? "Creating..." : "Proceed"}
								</Button>
								<Button type="button" variant="secondary" onClick={handleClose} className = "mt-2 p-2">
									Cancel
								</Button>
							</>
						) : (
							<>
								<Button type="button"  disabled={isSubmitting} onClick={handleSubmit(onSubmit)} className = "mt-2 p-2">
									{isSubmitting ? "Creating..." : "Create"}
								</Button>
								<Button type="button"  variant="secondary" onClick={handleClose} className = "mt-2 p-2">
									Cancel
								</Button>
							</>
						)}
					</MDBCol>
				</MDBRow>
			</form>
		</AbsModal>
	);
};

export default CreateUKETSAerModal;
